import React, { useMemo } from 'react';
import { Box, Flex, Text, HStack, VStack, Icon } from '@chakra-ui/react';
import { FaCheck, FaTimes } from 'react-icons/fa';
import {
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts';

const FONT = "'DM Sans', sans-serif";
const GREEN = '#16a34a';
const CYAN = '#0891b2';
const INDIGO = '#6366f1';
const BORDER = '#e8e8e8';
const MUTED = '#aaaaaa';
const DARK = '#111111';
const LABEL_STYLE = {
  fontFamily: FONT,
  fontSize: '10px',
  fontWeight: 400,
  textTransform: 'uppercase',
  letterSpacing: '0.12em',
  color: MUTED,
  lineHeight: 1.2,
};

const parseInsightSections = (insight) => {
  if (!insight) return { competitiveAdvantages: [], quickWins: [] };
  const lines = insight.split('\n');
  const sections = { competitiveAdvantages: [], quickWins: [] };
  let currentSection = null;
  for (const line of lines) {
    if (line.includes('Competitive Advantages')) { currentSection = 'competitiveAdvantages'; continue; }
    if (line.includes('Quick Wins')) { currentSection = 'quickWins'; }
    if (currentSection && line.trim().startsWith('*')) {
      const content = line.trim().replace(/^\*\s*/, '');
      const boldRegex = /\*\*([^*]+)\*\*/g;
      const parts = [];
      let lastIndex = 0;
      let match;
      while ((match = boldRegex.exec(content)) !== null) {
        if (match.index > lastIndex) parts.push({ type: 'text', content: content.substring(lastIndex, match.index) });
        parts.push({ type: 'bold', content: match[1] });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < content.length) parts.push({ type: 'text', content: content.substring(lastIndex) });
      sections[currentSection].push({ parts, text: parts.map(p => p.content).join('') });
    }
  }
  return sections;
};

const calculateThreeMonthGrowth = (monthlyReviewCounts = {}, months = []) => {
  if (months.length < 3) return 0;
  return months.slice(-3).reduce((sum, month) => sum + (monthlyReviewCounts[month] || 0), 0);
};

function BusinessIntelCard({
  business,
  trafficData,
  onClick,
  onToggleFavorite,
  isFavorite,
  variant = 'card',
  glowOnHover = false,
}) {
  const historicalData = business.historicalData || {};
  const monthlyReviewCounts = useMemo(
    () => historicalData.monthlyReviewCounts || {},
    [historicalData.monthlyReviewCounts]
  );
  const sortedMonths = Object.keys(monthlyReviewCounts).sort();
  const threeMonthGrowth = calculateThreeMonthGrowth(monthlyReviewCounts, sortedMonths);

  const sentimentBreakdown = historicalData.sentimentBreakdown;
  const sentimentPercent = sentimentBreakdown
    ? ((sentimentBreakdown.positive /
        (sentimentBreakdown.positive + sentimentBreakdown.neutral + sentimentBreakdown.negative)) * 100).toFixed(0)
    : null;

  const trafficDisplay = business.traffic_data?.metrics?.estimated_monthly_visitors;
  const trafficStr = trafficDisplay
    ? (trafficDisplay >= 1000 ? `${(trafficDisplay / 1000).toFixed(1)}k` : String(trafficDisplay))
    : null;

  const cumulativeReviewsData = useMemo(() => {
    if (!monthlyReviewCounts || Object.keys(monthlyReviewCounts).length === 0) return [];
    const sortedData = Object.entries(monthlyReviewCounts)
      .map(([month, count]) => ({ date: new Date(month + '-01T00:00:00').getTime(), count, originalMonth: month }))
      .sort((a, b) => a.date - b.date)
      .slice(-12);
    const historicalTotal = sortedData.reduce((sum, item) => sum + (item.count || 0), 0);
    const actualTotal = business.rating_count || business.total_reviews || historicalTotal;
    const baselineReviews = Math.max(0, actualTotal - historicalTotal);
    let cumulative = baselineReviews;
    return sortedData.map(item => {
      cumulative += item.count || 0;
      return { month: item.originalMonth, cumulativeReviews: cumulative };
    });
  }, [monthlyReviewCounts, business.rating_count, business.total_reviews]);

  const insightSections = useMemo(() => parseInsightSections(business.insight), [business.insight]);

  const getProText = () => {
    if (insightSections.competitiveAdvantages.length === 0) return null;
    const text = insightSections.competitiveAdvantages[0].text || '';
    return text.length > 90 ? text.substring(0, 90) + '...' : text;
  };
  const getConText = () => {
    if (insightSections.quickWins.length === 0) return null;
    const text = insightSections.quickWins[0].text || '';
    return text.length > 90 ? text.substring(0, 90) + '...' : text;
  };

  const categoryLabel = business.primary_type && business.primary_type !== 'NONE'
    ? business.primary_type.replace(/_/g, ' ').toUpperCase()
    : null;

  const locationText = (() => {
    if (!business.address) return null;
    const parts = business.address.split(',');
    if (parts.length >= 2) {
      return parts.slice(1).join(',').trim();
    }
    return business.address;
  })();

  const serviceAreaText = (() => {
    if (!business.address) return null;
    const parts = business.address.split(',');
    const city = parts.length >= 2 ? parts[1].trim() : parts[0].trim();
    return `Serves ${city}`;
  })();

  const isHoverVariant = variant === 'hover';

  const uniqueId = useMemo(() => Math.random().toString(36).slice(2, 8), []);

  return (
    <Box
      fontFamily={FONT}
      maxW="340px"
      w="100%"
      bg="white"
      borderRadius="24px"
      border="1px solid"
      borderColor={BORDER}
      overflow="hidden"
      cursor={onClick ? 'pointer' : 'default'}
      onClick={onClick}
      transition="all 0.2s ease"
      _hover={onClick ? {
        transform: 'translateY(-2px)',
        borderColor: '#d0d0d0',
        ...(glowOnHover ? {
          boxShadow: '0 0 30px 10px rgba(49, 130, 206, 0.45), 0 0 60px 24px rgba(66, 153, 225, 0.25)',
        } : {}),
      } : undefined}
      role={onClick ? 'button' : undefined}
      aria-label={onClick ? `View details for ${business.name}` : undefined}
      position="relative"
    >
      {/* Gradient accent bar */}
      <Box
        h="5px"
        w="100%"
        bgGradient={`linear(to-r, ${GREEN}, ${CYAN}, ${INDIGO})`}
      />

      {/* Header */}
      <Box px={5} pt={4} pb={3} borderBottom="1px solid #f0f0f0">
        {categoryLabel && (
          <Text
            fontSize="10px"
            fontWeight={600}
            textTransform="uppercase"
            letterSpacing="0.12em"
            color={GREEN}
            mb={1}
            fontFamily={FONT}
          >
            {categoryLabel}
          </Text>
        )}
        <Text
          fontSize="18px"
          fontWeight={600}
          color={DARK}
          lineHeight="1.3"
          noOfLines={2}
          fontFamily={FONT}
        >
          {business.name}
        </Text>
        {locationText && (
          <Text
            fontSize="12px"
            fontWeight={400}
            color={MUTED}
            mt="2px"
            fontFamily={FONT}
          >
            {locationText}
          </Text>
        )}
      </Box>

      {/* Stats row - 3 columns */}
      <Flex borderBottom="1px solid #f0f0f0">
        {/* Reviews */}
        <Flex
          flex={1}
          direction="column"
          align="center"
          py={3}
          borderRight="1px solid #f0f0f0"
        >
          <Text fontSize="24px" fontWeight={600} color={DARK} lineHeight={1} fontFamily={FONT}>
            {parseInt(business.rating_count || business.total_reviews || 0).toLocaleString()}
          </Text>
          <Text {...LABEL_STYLE} mt="4px">REVIEWS</Text>
        </Flex>

        {/* Rating */}
        <Flex
          flex={1}
          direction="column"
          align="center"
          py={3}
          borderRight="1px solid #f0f0f0"
        >
          <Text fontSize="24px" fontWeight={600} color={DARK} lineHeight={1} fontFamily={FONT}>
            {business.rating_avg?.toFixed(1) || 'N/A'}
          </Text>
          <Text {...LABEL_STYLE} mt="4px">RATING</Text>
        </Flex>

        {/* Last 3mo */}
        <Flex
          flex={1}
          direction="column"
          align="center"
          py={3}
        >
          <Text
            fontSize="24px"
            fontWeight={600}
            color={threeMonthGrowth > 0 ? GREEN : DARK}
            lineHeight={1}
            fontFamily={FONT}
          >
            {threeMonthGrowth > 0 ? `+${threeMonthGrowth}` : String(threeMonthGrowth)}
          </Text>
          <Text {...LABEL_STYLE} mt="4px">LAST 3MO</Text>
        </Flex>
      </Flex>

      {/* Sentiment + Traffic row */}
      <Flex px={5} py={3} align="center" gap={3} borderBottom="1px solid #f0f0f0">
        {/* Sentiment bar */}
        <Box flex={1}>
          <HStack justify="space-between" mb="4px">
            <Text {...LABEL_STYLE}>SENTIMENT</Text>
            <Text fontSize="12px" fontWeight={600} color={DARK} fontFamily={FONT}>
              {sentimentPercent ? `${sentimentPercent}%` : 'N/A'}
            </Text>
          </HStack>
          <Box
            h="5px"
            w="100%"
            bg="#eeeeee"
            borderRadius="3px"
            overflow="hidden"
          >
            <Box
              h="100%"
              w={sentimentPercent ? `${sentimentPercent}%` : '0%'}
              bgGradient={`linear(to-r, ${GREEN}, ${CYAN})`}
              borderRadius="3px"
              transition="width 0.6s ease"
            />
          </Box>
        </Box>

        {/* Traffic pill */}
        <Flex
          direction="column"
          align="center"
          bg="#f7f7f7"
          borderRadius="10px"
          px={3}
          py={2}
          minW="70px"
        >
          <Text fontSize="18px" fontWeight={600} color={CYAN} lineHeight={1} fontFamily={FONT}>
            {trafficStr || 'N/A'}
          </Text>
          <Text {...LABEL_STYLE} mt="2px">VISITS/MO</Text>
        </Flex>
      </Flex>

      {/* Sparklines */}
      <Box px={5} py={3}>
        {/* Review Growth */}
        <Box mb={3}>
          <Text {...LABEL_STYLE} mb="6px">REVIEW GROWTH</Text>
          <Box h="50px">
            {cumulativeReviewsData.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={cumulativeReviewsData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`reviewFill-${uniqueId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={GREEN} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={GREEN} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="cumulativeReviews"
                    stroke={GREEN}
                    strokeWidth={2}
                    fill={`url(#reviewFill-${uniqueId})`}
                    dot={false}
                    isAnimationActive={!isHoverVariant}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Flex h="100%" align="center" justify="center">
                <Text fontSize="10px" color={MUTED} fontFamily={FONT}>No data</Text>
              </Flex>
            )}
          </Box>
        </Box>

        {/* Organic Traffic */}
        <Box>
          <Text {...LABEL_STYLE} mb="6px">ORGANIC TRAFFIC</Text>
          <Box h="50px">
            {trafficData && trafficData.length > 1 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trafficData} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={`trafficFill-${uniqueId}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={CYAN} stopOpacity={0.2} />
                      <stop offset="100%" stopColor={CYAN} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area
                    type="monotone"
                    dataKey="organicClicks"
                    stroke={CYAN}
                    strokeWidth={2}
                    fill={`url(#trafficFill-${uniqueId})`}
                    dot={false}
                    isAnimationActive={!isHoverVariant}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <Flex h="100%" align="center" justify="center">
                <Text fontSize="10px" color={MUTED} fontFamily={FONT}>No data</Text>
              </Flex>
            )}
          </Box>
        </Box>
      </Box>

      {/* Service area footer */}
      {serviceAreaText && (
        <Flex px={5} pb={3} align="center" gap={2}>
          <Box w="8px" h="8px" borderRadius="full" bg={GREEN} flexShrink={0} />
          <Text fontSize="12px" fontWeight={400} color={MUTED} fontFamily={FONT}>
            {serviceAreaText}
          </Text>
        </Flex>
      )}

      {/* Pros & Cons */}
      <Box px={5} pb={4} pt={1} borderTop="1px solid #f0f0f0">
        <VStack spacing={2} align="stretch">
          {/* Pro */}
          <Box
            bg="#f0fdf4"
            borderRadius="10px"
            px={3}
            py="10px"
          >
            <HStack spacing={2} align="flex-start">
              <Flex
                w="18px" h="18px"
                borderRadius="full"
                bg={GREEN}
                justify="center"
                align="center"
                flexShrink={0}
                mt="1px"
              >
                <Icon as={FaCheck} color="white" boxSize="8px" />
              </Flex>
              <Box flex={1}>
                <Text
                  fontSize="10px"
                  fontWeight={600}
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                  color={GREEN}
                  mb="2px"
                  fontFamily={FONT}
                >
                  Strength
                </Text>
                <Text fontSize="12px" fontWeight={400} color="#374151" lineHeight="1.4" fontFamily={FONT}>
                  {getProText() || (
                    <Text as="span" color={MUTED} fontStyle="italic">Analysis pending...</Text>
                  )}
                </Text>
              </Box>
            </HStack>
          </Box>

          {/* Con */}
          <Box
            bg="#fef2f2"
            borderRadius="10px"
            px={3}
            py="10px"
          >
            <HStack spacing={2} align="flex-start">
              <Flex
                w="18px" h="18px"
                borderRadius="full"
                bg="#ef4444"
                justify="center"
                align="center"
                flexShrink={0}
                mt="1px"
              >
                <Icon as={FaTimes} color="white" boxSize="8px" />
              </Flex>
              <Box flex={1}>
                <Text
                  fontSize="10px"
                  fontWeight={600}
                  textTransform="uppercase"
                  letterSpacing="0.1em"
                  color="#ef4444"
                  mb="2px"
                  fontFamily={FONT}
                >
                  Opportunity
                </Text>
                <Text fontSize="12px" fontWeight={400} color="#374151" lineHeight="1.4" fontFamily={FONT}>
                  {getConText() || (
                    <Text as="span" color={MUTED} fontStyle="italic">Analysis pending...</Text>
                  )}
                </Text>
              </Box>
            </HStack>
          </Box>
        </VStack>
      </Box>
    </Box>
  );
}

export default BusinessIntelCard;
